import { useEffect } from "react"
import type { SetURLSearchParams } from "react-router"

/**
 * Keeps a row selected whenever rows are available, so the detail panel
 * never sits collapsed with nothing to show.
 *
 * Pass `ready=false` while `rows` still holds another query's results (react-query
 * `keepPreviousData`). Seeding from stale rows would pin `?id=` to a row that isn't
 * in the list being loaded — and since the detail panel keys off that param alone,
 * it would keep rendering it even when the settled list is empty.
 */
export function useDefaultRowSelection<T extends { id: string }>(
  rows: T[],
  selectedId: string | null,
  setSearchParams: SetURLSearchParams,
  ready = true,
) {
  useEffect(() => {
    if (!ready || selectedId || rows.length === 0) return
    const firstId = rows[0].id
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.set("id", firstId)
        return next
      },
      { replace: true },
    )
  }, [ready, selectedId, rows, setSearchParams])
}
